import { useState, useEffect, useRef } from "react";
import { Mic, Square, Send, X } from "lucide-react";
import { useSpeechRecognition } from "../hooks/useSpeech";
import { useT, useLang } from "../lib/i18n";

export const QuestionBox = ({
  onSubmit,
  placeholder,
  initialValue = "",
  autoStartMic = false,
  buttonLabel,
  testIdPrefix = "main",
}) => {
  const t = useT();
  const { lang } = useLang();
  const [value, setValue] = useState(initialValue);
  const { supported, listening, transcript, start, stop } = useSpeechRecognition(lang);
  const taRef = useRef(null);
  const ph = placeholder || t("qb_placeholder");
  const btn = buttonLabel || t("qb_ask");

  useEffect(() => {
    if (listening && transcript) setValue(transcript);
  }, [transcript, listening]);

  useEffect(() => {
    if (autoStartMic && supported) start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = () => {
    const q = value.trim();
    if (!q) return;
    stop();
    onSubmit(q);
  };

  const toggleMic = () => {
    if (listening) stop();
    else start();
  };

  return (
    <div className="w-full">
      <div
        className={`relative bg-white rounded-2xl border-2 transition-all ${
          listening
            ? "border-rose-400 ring-4 ring-rose-100"
            : "border-teal-500 focus-within:ring-4 focus-within:ring-teal-100"
        } shadow-lg`}
      >
        <textarea
          ref={taRef}
          data-testid={`${testIdPrefix}-question-input`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={listening ? t("qb_listening_placeholder") : ph}
          rows={2}
          className="w-full resize-none bg-transparent px-5 py-4 pr-14 text-lg sm:text-xl text-slate-900 placeholder:text-slate-400 outline-none rounded-2xl leading-relaxed min-h-[96px]"
        />
        {value && (
          <button
            onClick={() => setValue("")}
            data-testid={`${testIdPrefix}-clear-button`}
            aria-label="Clear"
            className="absolute top-3 right-3 p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {listening && (
        <p className="mt-2 text-base text-rose-600 font-semibold flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" />
          {t("qb_listening_hint")}
        </p>
      )}

      <div className="mt-4 flex flex-col sm:flex-row gap-3">
        <button
          onClick={submit}
          data-testid={`${testIdPrefix}-ask-button`}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold text-lg px-6 py-4 rounded-xl transition-transform active:scale-[0.98] shadow-md min-h-[56px]"
        >
          <Send className="w-5 h-5" /> {btn}
        </button>

        {supported && (
          <button
            onClick={toggleMic}
            data-testid={`${testIdPrefix}-speak-button`}
            className={`inline-flex items-center justify-center gap-2 font-bold text-lg px-6 py-4 rounded-xl transition-transform active:scale-[0.98] shadow-md min-h-[56px] ${
              listening
                ? "bg-rose-600 hover:bg-rose-700 text-white mic-listening"
                : "bg-white border-2 border-rose-500 text-rose-600 hover:bg-rose-50"
            }`}
          >
            {listening ? (
              <>
                <Square className="w-5 h-5" /> {t("qb_stop")}
              </>
            ) : (
              <>
                <Mic className="w-5 h-5" /> {t("qb_speak")}
              </>
            )}
          </button>
        )}
      </div>

      {!supported && (
        <p className="mt-2 text-sm text-slate-500">
          {t("qb_no_voice")}
        </p>
      )}
    </div>
  );
};
